import type React from 'react'

import type { PressOrigin } from '../types/press-origin'

/**
 * Dismiss-to-canvas on blur (WMPCM-9582 TC-01/TC-03): the same commit Escape and clicking away
 * to a DIFFERENT shape already trigger (via `center-canvas/hooks/use-edit-mode-key-capture.ts`'s key-capture effect and
 * `onSelectionChange`), fired here too so it also covers focus simply leaving this bar for
 * something that isn't a shape at all — a panel, the menubar, or empty pasteboard, none of
 * which necessarily change the canvas selection the way clicking another object does.
 *
 * Mirrors `use-shape-text-editing.ts`'s `handleBlur` rather than inventing a new check:
 * re-read focus a frame later via `document.activeElement` (not just `event.relatedTarget`,
 * which mouse-driven blurs don't set consistently across browsers), and bail if it landed on
 * this bar's own root OR a `[data-text-edit-keepalive]` control. The latter is required, not
 * optional — every portalled sub-control here (Font Family, Font Style, Font Size, Spacing,
 * Colour, Offset, this bar's own Position popover) renders outside `root` in the DOM via
 * `createPortal`/Radix's own portal, so opening one blurs the trigger button inside `root` and
 * moves focus to a node `root.contains()` cannot see; only the shared keepalive marker does.
 * Without it, opening the Colour popover would itself look like "left the toolbar" and commit
 * the bar shut while the user is still choosing a swatch.
 *
 * Attached on the root rather than on the textarea alone: `onBlur` is a bubbling synthetic
 * event in React, so a blur on ANY interactive descendant (a button, a slider thumb inside a
 * portalled popover) reaches this one handler — the check body only needs to answer "where is
 * focus now", not "which control fired this".
 *
 * A blur caused by a pointer press inside the bar never dismisses it (WMPCM-12967). Safari does not
 * focus a button when it is clicked, so pressing a submenu trigger left `document.activeElement` on
 * `<body>` with no `relatedTarget`, which the checks above read as "focus left the toolbar" and
 * closed the bar before the submenu could open. `pressOriginRef` is set by
 * `markPointerDownInside` on the root's capture phase, which also sees presses inside the portalled
 * popovers because React bubbles those through their React parent.
 *
 * Excusing a press on the bar's own markup must not leave focus on `<body>`: nothing inside the bar
 * would then hold focus, so a later click on a panel, the menubar or empty pasteboard produces no
 * blur and `onDismiss` never runs. When such a press left focus on `<body>`, `restoreFocus` puts it
 * back in the bar. A press INSIDE a popover is excused but never refocused: Radix closes a popover
 * when focus moves outside it, so pulling focus to the text field would close it mid-press and
 * lose the control's click. A popover that already took focus is left alone either way.
 */
export function createToolbarBlurHandler(
  rootRef: React.RefObject<HTMLDivElement | null>,
  onDismiss: () => void,
  pressOriginRef: { current: PressOrigin },
  restoreFocus: () => void
) {
  return (event: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null
    // Read now, not in the frame callback: the press that moved focus has been handled by then.
    const pressOrigin = pressOriginRef.current

    requestAnimationFrame(() => {
      if (pressOrigin !== 'none') {
        const active = document.activeElement

        if (pressOrigin === 'bar' && (!active || active === document.body)) restoreFocus()

        return
      }

      const focusedEl = relatedTarget ?? (document.activeElement as HTMLElement | null)

      if (focusedEl && rootRef.current?.contains(focusedEl)) return
      if (focusedEl?.closest?.('[data-text-edit-keepalive]')) return

      onDismiss()
    })
  }
}

/**
 * Records where a pointer press began, for `createToolbarBlurHandler`, clearing it on the next
 * task. The press's `mousedown` and the focus change it causes both run in the same task as this
 * `pointerdown`, so the origin is set exactly while the blur it explains is being handled;
 * clearing it afterwards stops a press that moved no focus (one inside the textarea) from excusing
 * a later, unrelated blur. A target outside `root` in the DOM can only be a portalled popover, as
 * React bubbles its events through the bar.
 */
export function markPointerDownInside(
  pressOriginRef: { current: PressOrigin },
  target: EventTarget,
  root: HTMLElement | null
) {
  pressOriginRef.current = target instanceof Node && root?.contains(target) ? 'bar' : 'popover'
  setTimeout(() => {
    pressOriginRef.current = 'none'
  }, 0)
}
